import { useCallback, useEffect, useState } from "react";
import { api, brl, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Landmark, Plus, Loader2, Check, RotateCcw, Trash2, Scale, BarChart3, FileText } from "lucide-react";
import { Link } from "react-router-dom";
import { FinanceOverviewCards } from "@/components/FinanceOverviewCards";
import { PageHead, Stat, Empty, Field, RangePicker, inputCls, selectCls, btnPrimary, btnGhost, num, fmtDate, todayIso, monthStartIso, addDaysIso } from "@/components/erp/ErpUi";

export const CATEGORY_LABEL = { pecas: "Peças", terceiros: "Terceirizados", aluguel: "Aluguel", salarios: "Salários", comissoes: "Comissões", impostos: "Impostos", outros: "Outros", os: "Ordens de serviço" };
const TABS = [{ k: "receber", l: "A receber" }, { k: "pagar", l: "A pagar" }, { k: "fluxo", l: "Fluxo de caixa" }];

function EntryDialog({ open, onOpenChange, kind, onSaved }) {
  const cats = kind === "pagar" ? ["aluguel", "salarios", "comissoes", "impostos", "pecas", "outros"] : ["outros", "os"];
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const [centers, setCenters] = useState([]);
  useEffect(() => { if (open) setF({ category: cats[0], description: "", amount: "", due_date: todayIso(), installments: "1", paid: false, cost_center: "" }); }, [open, kind]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (open && kind === "pagar") api.get("/erp/cost-centers").then(({ data }) => setCenters(data)).catch(() => setCenters([])); }, [open, kind]);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/erp/finance", { ...f, kind, amount: num(f.amount), installments: Number(f.installments) || 1, cost_center: (f.cost_center || null) }); toast.success("Lançamento criado"); onSaved(); onOpenChange(false); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="finance-dialog" className="bg-zinc-950 border border-zinc-800 text-zinc-100 max-w-md max-h-[90dvh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display uppercase">{kind === "pagar" ? "Nova conta a pagar" : "Nova conta a receber"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid grid-cols-2 gap-3">
          <Field label="Categoria" className="col-span-2">
            <select data-testid="finance-category" value={f.category || ""} onChange={set("category")} className={selectCls}>{cats.map((c) => <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>)}</select>
          </Field>
          <Field label="Descrição" className="col-span-2"><Input data-testid="finance-description" value={f.description || ""} onChange={set("description")} className={inputCls} /></Field>
          <Field label="Valor total (R$)"><Input data-testid="finance-amount" value={f.amount || ""} onChange={set("amount")} inputMode="decimal" className={inputCls} /></Field>
          <Field label="Parcelas"><Input data-testid="finance-installments" value={f.installments || ""} onChange={set("installments")} inputMode="numeric" className={inputCls} /></Field>
          <Field label="1º vencimento" className="col-span-2"><input data-testid="finance-due" type="date" value={f.due_date || ""} onChange={set("due_date")} className={selectCls} /></Field>
          {kind === "pagar" && (
            <Field label="Centro de custo (opcional)" className="col-span-2">
              <select data-testid="finance-cost-center" value={f.cost_center || ""} onChange={set("cost_center")} className={selectCls}>
                <option value="">— sem centro —</option>
                {centers.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
              {centers.length === 0 && <p className="text-[11px] text-zinc-500 mt-1">Cadastre em Configurações › Financeiro.</p>}
            </Field>
          )}
          <label className="col-span-2 flex items-center gap-2 text-sm text-zinc-300"><input data-testid="finance-paid" type="checkbox" checked={!!f.paid} onChange={set("paid")} /> Já foi pago</label>
          <div className="col-span-2 flex justify-end gap-2">
            <Button type="button" variant="outline" className={btnGhost} onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button data-testid="finance-save" type="submit" disabled={busy || !f.description || !num(f.amount)} className={btnPrimary}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function BoletoDialog({ open, onOpenChange, onSaved }) {
  const [line, setLine] = useState("");
  const [parsed, setParsed] = useState(null);
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [scanning, setScanning] = useState(false);
  useEffect(() => { if (open) { setLine(""); setParsed(null); setDescription(""); } }, [open]);
  const parse = async (val) => {
    const v = (val || line).replace(/\D/g, "");
    if (v.length !== 47 && v.length !== 44) { toast.error("Precisa de 47 dígitos (linha digitável) ou 44 (código de barras)."); return; }
    try { const { data } = await api.post("/erp/finance/boleto/parse", { line: v }); setParsed(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const scan = async () => {
    if (typeof window === "undefined" || !("BarcodeDetector" in window)) { toast.error("Seu navegador não tem leitor de código de barras. Digite ou cole a linha digitável."); return; }
    try {
      setScanning(true);
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      const video = document.createElement("video"); video.srcObject = stream; await video.play();
      // eslint-disable-next-line no-undef
      const det = new BarcodeDetector({ formats: ["itf", "code_128"] });
      const start = Date.now(); let found = null;
      while (Date.now() - start < 15000 && !found) {
        try { const codes = await det.detect(video); if (codes?.[0]?.rawValue) { found = codes[0].rawValue; break; } } catch { /* ignore */ }
        await new Promise((r) => setTimeout(r, 300));
      }
      stream.getTracks().forEach((t) => t.stop());
      if (found) { setLine(found); await parse(found); }
      else { toast.error("Não consegui ler o código. Mire bem no código de barras."); }
    } catch (e) { toast.error(`Camera: ${e?.message || e}`); } finally { setScanning(false); }
  };
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      if (!parsed.due_date) { toast.error("Informe o vencimento."); setBusy(false); return; }
      await api.post("/erp/finance", { kind: "pagar", category: "outros", description: (description || `Boleto ${(parsed.line || "").slice(-6)}`).trim(), amount: parsed.amount, due_date: parsed.due_date, installments: 1, paid: false });
      toast.success("Boleto cadastrado em Contas a pagar"); onSaved(); onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="boleto-dialog" className="bg-zinc-950 border border-zinc-800 text-zinc-100 max-w-md">
        <DialogHeader><DialogTitle className="font-display uppercase">Novo boleto</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Linha digitável ou código de barras">
            <Input data-testid="boleto-line" value={line} onChange={(e) => setLine(e.target.value)} placeholder="47 dígitos" className={inputCls} />
          </Field>
          <div className="flex gap-2 flex-wrap">
            <Button type="button" data-testid="boleto-parse" onClick={() => parse()} className={btnPrimary}>Ler</Button>
            <Button type="button" data-testid="boleto-scan" variant="outline" disabled={scanning} onClick={scan} className={btnGhost}>{scanning ? <Loader2 className="h-4 w-4 animate-spin" /> : "Escanear com câmera"}</Button>
          </div>
          {parsed && (
            <form onSubmit={save} className="grid grid-cols-2 gap-3 pt-2 border-t border-zinc-800">
              <Field label="Valor (R$)"><Input data-testid="boleto-amount" value={parsed.amount.toFixed(2).replace(".", ",")} readOnly className={inputCls} /></Field>
              <Field label="Vencimento"><Input data-testid="boleto-due" type="date" value={parsed.due_date} onChange={(e) => setParsed((p) => ({ ...p, due_date: e.target.value }))} className={inputCls} /></Field>
              <Field label="Descrição" className="col-span-2"><Input data-testid="boleto-description" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Ex.: boleto energia dez/2026" className={inputCls} /></Field>
              <div className="col-span-2 flex justify-end gap-2">
                <Button type="button" variant="outline" className={btnGhost} onClick={() => onOpenChange(false)}>Cancelar</Button>
                <Button data-testid="boleto-save" type="submit" disabled={busy} className={btnPrimary}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar boleto"}</Button>
              </div>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PayDialog({ open, onOpenChange, entry, onSaved }) {
  const overdue = entry && entry.due_date && (entry.due_date < new Date().toISOString().slice(0, 10));
  const [interest, setInterest] = useState("");
  const [fine, setFine] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setInterest(""); setFine(""); } }, [open]);
  const save = async () => {
    setBusy(true);
    try { await api.post(`/erp/finance/${entry.id}/pay`, { interest: num(interest) || null, fine: num(fine) || null }); toast.success("Dado baixa"); onSaved(); onOpenChange(false); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  if (!entry) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="pay-dialog" className="bg-zinc-950 border border-zinc-800 text-zinc-100 max-w-sm">
        <DialogHeader><DialogTitle className="font-display uppercase">Dar baixa</DialogTitle></DialogHeader>
        <div className="text-xs text-zinc-400">{entry.description} · {brl(entry.amount)} · vence {fmtDate(entry.due_date)}{overdue ? " (vencido)" : ""}</div>
        {overdue && (
          <div className="grid grid-cols-2 gap-2 pt-2">
            <Field label="Juros pagos (R$)"><Input data-testid="pay-interest" value={interest} onChange={(e) => setInterest(e.target.value)} inputMode="decimal" className={inputCls} /></Field>
            <Field label="Multa paga (R$)"><Input data-testid="pay-fine" value={fine} onChange={(e) => setFine(e.target.value)} inputMode="decimal" className={inputCls} /></Field>
            <p className="col-span-2 text-[11px] text-zinc-500">Esses valores entram como despesa automática (categoria Outros).</p>
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" className={btnGhost} onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button data-testid="pay-confirm" disabled={busy} onClick={save} className={btnPrimary}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirmar baixa"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EntryList({ kind, range, scopeAll }) {
  const [status, setStatus] = useState("aberto");
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [boletoOpen, setBoletoOpen] = useState(false);
  const [payEntry, setPayEntry] = useState(null);
  const load = useCallback(() => api.get("/erp/finance", { params: { kind, status: status || undefined, from: range[0], to: range[1] } }).then(({ data }) => setData(data)).catch((e) => toast.error(formatApiError(e))), [kind, status, range]);
  useEffect(() => { load(); }, [load]);
  const act = async (fn) => { try { await fn(); load(); } catch (e) { toast.error(formatApiError(e)); } };
  const s = data?.summary;
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <Stat testid={`${kind}-open`} label="Em aberto" value={s ? brl(kind === "pagar" ? s.pagar_aberto : s.receber_aberto) : "…"} tone="amber" />
        <Stat testid={`${kind}-paid`} label={kind === "pagar" ? "Pago" : "Recebido"} value={s ? brl(kind === "pagar" ? s.pagar_pago : s.receber_pago) : "…"} tone="green" />
        <Stat testid={`${kind}-overdue`} label="Vencidos" value={s?.overdue ?? "…"} tone="red" />
      </div>
      <div className="flex gap-2 flex-wrap">
        <select data-testid={`${kind}-status`} value={status} onChange={(e) => setStatus(e.target.value)} className={`${selectCls} w-auto`}>
          <option value="aberto">Em aberto</option><option value="pago">Pagos</option><option value="">Todos</option>
        </select>
        <Button data-testid={`${kind}-new-btn`} disabled={scopeAll} className={btnPrimary} onClick={() => setOpen(true)}><Plus className="h-4 w-4 mr-1" /> Lançar</Button>
        {kind === "pagar" && <Button data-testid="pagar-boleto-btn" disabled={scopeAll} variant="outline" className={btnGhost} onClick={() => setBoletoOpen(true)}><FileText className="h-4 w-4 mr-1" /> Novo boleto</Button>}
      </div>
      {!data ? <Loader2 className="h-6 w-6 animate-spin text-green-brand mx-auto" /> : data.items.length === 0 ? <Empty>Nada no período.</Empty> : (
        <ul className="space-y-2">
          {data.items.map((e) => (
            <li key={e.id} data-testid={`finance-row-${e.id}`} className={`rounded-xl border p-3 ${e.overdue ? "border-red-900 bg-red-950/20" : "border-zinc-800 bg-zinc-950/60"}`}>
              <div className="flex justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm text-zinc-100 font-medium truncate">{e.description}</div>
                  <div className="text-[11px] text-zinc-500">{CATEGORY_LABEL[e.category] || e.category} · vence {fmtDate(e.due_date)}{e.overdue ? " · VENCIDO" : ""}{e.status === "pago" ? ` · pago ${fmtDate((e.paid_at || "").slice(0, 10))}` : ""}{scopeAll && e.shop_name ? ` · ${e.shop_name}` : ""}</div>
                </div>
                <div className={`font-display font-black whitespace-nowrap ${kind === "pagar" ? "text-red-300" : "text-green-neon"}`}>{brl(e.amount)}</div>
              </div>
              <div className="mt-2 flex gap-2">
                {e.status === "aberto"
                  ? <Button data-testid={`finance-pay-${e.id}`} size="sm" className="h-8 bg-green-brand hover:bg-green-brandDark text-white text-xs font-bold uppercase" onClick={() => setPayEntry(e)}><Check className="h-3.5 w-3.5 mr-1" /> Dar baixa</Button>
                  : <Button data-testid={`finance-reopen-${e.id}`} size="sm" variant="outline" className={`h-8 text-xs ${btnGhost}`} onClick={() => act(() => api.post(`/erp/finance/${e.id}/reopen`))}><RotateCcw className="h-3.5 w-3.5 mr-1" /> Reabrir</Button>}
                {!e.auto && <Button data-testid={`finance-delete-${e.id}`} size="sm" variant="outline" className={`h-8 text-xs ${btnGhost}`} onClick={() => window.confirm("Excluir lançamento?") && act(() => api.delete(`/erp/finance/${e.id}`))}><Trash2 className="h-3.5 w-3.5" /></Button>}
              </div>
            </li>
          ))}
        </ul>
      )}
      <EntryDialog open={open} onOpenChange={setOpen} kind={kind} onSaved={load} />
      <BoletoDialog open={boletoOpen} onOpenChange={setBoletoOpen} onSaved={load} />
      <PayDialog open={!!payEntry} onOpenChange={(o) => !o && setPayEntry(null)} entry={payEntry} onSaved={load} />
    </div>
  );
}

function CashFlow({ range, scope }) {
  const [group, setGroup] = useState("day");
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/erp/cashflow", { params: { from: range[0], to: range[1], group, scope } }).then(({ data }) => setData(data)).catch((e) => toast.error(formatApiError(e)));
  }, [range, group, scope]);
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat testid="cash-balance" label="Saldo realizado" value={data ? brl(data.balance) : "…"} tone={data?.balance < 0 ? "red" : "green"} />
        <Stat testid="cash-open-in" label="A receber até o fim" value={data ? brl(data.open_in) : "…"} />
        <Stat testid="cash-open-out" label="A pagar até o fim" value={data ? brl(data.open_out) : "…"} tone="red" />
        <Stat testid="cash-forecast" label="Saldo previsto" value={data ? brl(data.forecast_balance) : "…"} tone={data?.forecast_balance < 0 ? "red" : "amber"} />
      </div>
      <select data-testid="cash-group" value={group} onChange={(e) => setGroup(e.target.value)} className={`${selectCls} w-auto`}><option value="day">Por dia</option><option value="month">Por mês</option></select>
      {!data ? <Loader2 className="h-6 w-6 animate-spin text-green-brand mx-auto" /> : data.rows.length === 0 ? <Empty>Sem movimento no período.</Empty> : (
        <div className="rounded-2xl border border-zinc-800 overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]">
            <thead><tr className="bg-zinc-900 text-[11px] font-mono-caps text-zinc-500 uppercase">
              <th className="text-left px-3 py-2">Período</th><th className="text-right px-3 py-2">Entradas</th><th className="text-right px-3 py-2">Saídas</th><th className="text-right px-3 py-2">Resultado</th><th className="text-right px-3 py-2">Previsto +</th><th className="text-right px-3 py-2">Previsto −</th>
            </tr></thead>
            <tbody>{data.rows.map((r) => (
              <tr key={r.period} data-testid={`cash-row-${r.period}`} className="border-t border-zinc-800">
                <td className="px-3 py-2 text-zinc-300">{r.period.length === 7 ? `${r.period.slice(5)}/${r.period.slice(0, 4)}` : fmtDate(r.period)}</td>
                <td className="px-3 py-2 text-right text-green-neon">{brl(r.in)}</td><td className="px-3 py-2 text-right text-red-300">{brl(r.out)}</td>
                <td className={`px-3 py-2 text-right font-bold ${r.net < 0 ? "text-red-300" : "text-zinc-100"}`}>{brl(r.net)}</td>
                <td className="px-3 py-2 text-right text-zinc-400">{brl(r.forecast_in)}</td><td className="px-3 py-2 text-right text-zinc-400">{brl(r.forecast_out)}</td>
              </tr>))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function OwnerFinance() {
  const { user } = useAuth();
  const scopeAll = user?.shop_id === "all";
  const [tab, setTab] = useState("receber");
  const [range, setRange] = useState([monthStartIso(), addDaysIso(60)]);
  return (
    <div className="space-y-5 animate-fade-up">
      <PageHead icon={Landmark} kicker="Financeiro" title="Contas e caixa">
        <Link to="/owner/dre" data-testid="finance-dre-link" className={`${btnGhost} h-9 px-3 inline-flex items-center rounded-md border`}><BarChart3 className="h-4 w-4 mr-1" /> DRE do mês</Link>
        <Link to="/owner/conciliar" data-testid="finance-reconcile-link" className={`${btnGhost} h-9 px-3 inline-flex items-center rounded-md border`}><Scale className="h-4 w-4 mr-1" /> Conciliar extrato</Link>
        <RangePicker from={range[0]} to={range[1]} onChange={setRange} testid="finance-range" /></PageHead>
      <FinanceOverviewCards />
      {scopeAll && <p className="text-xs text-amber-300">Visão somada de todas as unidades. Para lançar, escolha uma unidade no cabeçalho.</p>}
      <div className="flex gap-2 flex-wrap">
        {TABS.map((t) => <Button key={t.k} data-testid={`finance-tab-${t.k}`} variant="outline" onClick={() => setTab(t.k)} className={tab === t.k ? "bg-amber-brand text-black font-bold hover:bg-amber-brandDark" : btnGhost}>{t.l}</Button>)}
      </div>
      {tab === "fluxo" ? <CashFlow range={range} scope={scopeAll ? "all" : "current"} /> : <EntryList key={tab} kind={tab} range={range} scopeAll={scopeAll} />}
    </div>
  );
}
